import { useState } from 'react';
import { Search, X, SlidersHorizontal } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { GPUBrand, performanceTiers } from '@/data/gpuData';
import { cn } from '@/lib/utils';

export interface GPUFilters {
  search: string;
  brands: GPUBrand[];
  priceRange: [number, number];
  tiers: string[];
}

interface FilterBarProps {
  filters: GPUFilters;
  onFiltersChange: (filters: GPUFilters) => void;
  maxPrice: number;
}

export const FilterBar = ({ filters, onFiltersChange, maxPrice }: FilterBarProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const brands: { id: GPUBrand; label: string; color: string }[] = [
    { id: 'nvidia', label: 'NVIDIA', color: 'bg-nvidia' },
    { id: 'amd', label: 'AMD', color: 'bg-amd' },
    { id: 'intel', label: 'Intel', color: 'bg-intel' },
  ];

  const toggleBrand = (brand: GPUBrand) => {
    const newBrands = filters.brands.includes(brand)
      ? filters.brands.filter(b => b !== brand)
      : [...filters.brands, brand];
    onFiltersChange({ ...filters, brands: newBrands });
  };

  const toggleTier = (tierId: string) => {
    const newTiers = filters.tiers.includes(tierId)
      ? filters.tiers.filter(t => t !== tierId)
      : [...filters.tiers, tierId];
    onFiltersChange({ ...filters, tiers: newTiers });
  };

  const clearFilters = () => {
    onFiltersChange({
      search: '',
      brands: [],
      priceRange: [0, maxPrice],
      tiers: [],
    });
  };

  const activeFilterCount =
    filters.brands.length +
    filters.tiers.length +
    (filters.search ? 1 : 0) +
    (filters.priceRange[0] > 0 || filters.priceRange[1] < maxPrice ? 1 : 0);

  return (
    <div className="sticky top-16 z-20 bg-background/95 backdrop-blur-sm border-b py-4">
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
          {/* Search Input */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search GPUs (e.g. RTX 4090, RX 7800)"
              value={filters.search}
              onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
              className="pl-10 bg-card"
            />
            {filters.search && (
              <button
                onClick={() => onFiltersChange({ ...filters, search: '' })}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Brand Toggles */}
          <div className="flex items-center gap-2">
            {brands.map((brand) => (
              <button
                key={brand.id}
                onClick={() => toggleBrand(brand.id)}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 rounded-lg border transition-all text-sm font-medium',
                  filters.brands.length === 0 || filters.brands.includes(brand.id)
                    ? 'bg-card border-border hover:border-primary/50'
                    : 'bg-muted/50 border-transparent text-muted-foreground opacity-50'
                )}
                aria-label={`Filter by ${brand.label}`}
                aria-pressed={filters.brands.includes(brand.id)}
              >
                <div className={cn('w-2.5 h-2.5 rounded-full', brand.color)} />
                <span className="hidden sm:inline">{brand.label}</span>
              </button>
            ))}
          </div>

          {/* Advanced Filters Popover */}
          <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="gap-2 bg-card" aria-label="Open filter options">
                <SlidersHorizontal className="w-4 h-4" />
                <span className="hidden sm:inline">Filters</span>
                {activeFilterCount > 0 && (
                  <Badge variant="secondary" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 bg-card border shadow-lg" align="end">
              <div className="space-y-6">
                {/* Price Range */}
                <div>
                  <h4 className="font-medium text-sm mb-3">Price Range</h4>
                  <div className="px-2">
                    <Slider
                      value={filters.priceRange}
                      onValueChange={(value) =>
                        onFiltersChange({ ...filters, priceRange: value as [number, number] })
                      }
                      min={0}
                      max={maxPrice}
                      step={50}
                      className="mb-2"
                    />
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>${filters.priceRange[0]}</span>
                      <span>${filters.priceRange[1]}</span>
                    </div>
                  </div>
                </div>

                {/* Performance Tiers */}
                <div>
                  <h4 className="font-medium text-sm mb-3">Performance Tier</h4>
                  <div className="space-y-2">
                    {performanceTiers.map((tier) => (
                      <label
                        key={tier.id}
                        className="flex items-center gap-3 cursor-pointer"
                      >
                        <Checkbox
                          checked={filters.tiers.includes(tier.id)}
                          onCheckedChange={() => toggleTier(tier.id)}
                        />
                        <div className="flex items-center gap-2">
                          <div
                            className={cn(
                              'w-2.5 h-2.5 rounded-full',
                              tier.color === 'ultra' && 'bg-tier-ultra',
                              tier.color === 'high' && 'bg-tier-high',
                              tier.color === 'mid' && 'bg-tier-mid',
                              tier.color === 'entry' && 'bg-tier-entry'
                            )}
                          />
                          <span className="text-sm">{tier.name}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Clear Filters */}
                {activeFilterCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="w-full"
                  >
                    Clear All Filters
                  </Button>
                )}
              </div>
            </PopoverContent>
          </Popover>

          {/* Clear All Button (visible when filters active) */}
          {activeFilterCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-muted-foreground hover:text-foreground hidden md:flex"
            >
              <X className="w-4 h-4 mr-1" />
              Clear
            </Button>
          )}
        </div>

        {/* Active Filter Tags */}
        {activeFilterCount > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {filters.search && (
              <Badge variant="secondary" className="gap-1">
                Search: {filters.search}
                <button onClick={() => onFiltersChange({ ...filters, search: '' })} aria-label="Remove search filter">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            )}
            {filters.brands.map((brand) => (
              <Badge key={brand} variant="secondary" className="gap-1 capitalize">
                {brand}
                <button onClick={() => toggleBrand(brand)} aria-label={`Remove ${brand} filter`}>
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
            {filters.tiers.map((tierId) => {
              const tier = performanceTiers.find((t) => t.id === tierId);
              return tier ? (
                <Badge key={tierId} variant="secondary" className="gap-1">
                  {tier.name}
                  <button onClick={() => toggleTier(tierId)} aria-label={`Remove ${tier?.name} filter`}>
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ) : null;
            })}
            {(filters.priceRange[0] > 0 || filters.priceRange[1] < maxPrice) && (
              <Badge variant="secondary" className="gap-1">
                ${filters.priceRange[0]} - ${filters.priceRange[1]}
                <button
                  onClick={() =>
                    onFiltersChange({ ...filters, priceRange: [0, maxPrice] })
                  }
                  aria-label="Remove price range filter"
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
